import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { z } from "zod";

import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import { api } from "~/shared/services/api/fetch-client.ts";

import type { CreateFTLShipmentCommand } from "../create-ftl-shipment";
import { mapCreateFtlShipmentErrorToUserMessage } from "./use-create-ftl-shipment-error-mapper.ts";

const createFtlQuoteErrorSchema = z.array(
	z.object({
		code: z.string(),
		message: z.string(),
	}),
);

// Zod schema for created shipment response validation
const createdQuoteSchema = z.object({
	id: z.coerce.number(),
});

export type CreatedQuote = z.infer<typeof createdQuoteSchema>;

type UseCreateFtlQuoteOptions = {
	onSuccess?: (data: CreatedQuote, variables: CreateFTLShipmentCommand) => void;
	onError?: () => void;
};

async function submitShipmentQuote(
	request: CreateFTLShipmentCommand,
): Promise<CreatedQuote> {
	const response = await api.post("ftl-quote/shipment", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request),
		hooks: {
			beforeError: [
				async (error) => {
					const { response } = error;
					if (response) {
						const contentType = response.headers.get("content-type") ?? "";
						if (!contentType.toLowerCase().includes("application/json")) {
							return error;
						}

						try {
							const responseBody: unknown = await response.clone().json();
							const parsedError =
								createFtlQuoteErrorSchema.safeParse(responseBody);
							if (parsedError.success) {
								const messages = parsedError.data
									.map((e) => e.message)
									.join(" ");
								error.message = messages;
							}
						} catch {
							// Keep the original HTTP error so downstream status-based mapping still works.
							return error;
						}
					}

					return error;
				},
			],
		},
	});
	const data = await response.json();

	return createdQuoteSchema.parse(data);
}

export function useCreateFtlShipment(options?: UseCreateFtlQuoteOptions) {
	const { showSuccess, showError } = useNotifications();
	const consecutiveFailureCountRef = useRef(0);

	const { data, error, isPending, mutate } = useMutation({
		mutationFn: submitShipmentQuote,
		onSuccess: (data, variables) => {
			consecutiveFailureCountRef.current = 0;

			// Handle successful creation
			showSuccess(
				`FTL quote created successfully! Quote ID: ${data.id}. Shipment details have been emailed to your account rep for a manual quote.`,
			);

			// Call custom onSuccess callback if provided
			if (options?.onSuccess) {
				options.onSuccess(data, variables);
			}
		},
		onError: async (error) => {
			consecutiveFailureCountRef.current += 1;

			showError(
				await mapCreateFtlShipmentErrorToUserMessage(
					error,
					consecutiveFailureCountRef.current,
				),
			);
			options?.onError?.();
		},
	});

	return {
		data,
		error,
		isPending,
		createFtlShipment: mutate,
	};
}
